import api from "../utils/api";
import {
  ORGS_REQUEST,
  ORGS_SUCCESS,
  ORGS_FAILURE,
  OPTIONS_REQUEST,
  OPTIONS_SUCCESS,
  OPTIONS_FAILURE,
  SUMMARY_REQUEST,
  SUMMARY_SUCCESS,
  SUMMARY_FAILURE,
  SET_FILTER,
  SET_ME,
} from "./constants";

// Patch one or more filter values, e.g. dispatch(setFilter({ location_id: "3" })).
// The reducer handles cascading resets of dependent scopes.
export const setFilter = (patch) => ({ type: SET_FILTER, payload: patch });

// Load the signed-in admin's identity (role + organization). Decides whether the
// Organization filter is shown (platform admin) or hidden and locked to the
// caller's own org (organization admin). Auth failures are handled elsewhere — the
// api interceptor / ProtectedRoute end the session on 401/403 — so here we only
// record a successful identity.
export const fetchMe = () => async (dispatch) => {
  try {
    const { data } = await api.get("/api/me");
    dispatch({ type: SET_ME, payload: data });
  } catch (err) {
    // Leave `me` unloaded; the dashboard stays in its default (unscoped) state and
    // the backend still enforces scope on every response.
  }
};

// Drop blank/undefined filters so the backend sees only the ones that are set.
const paramsOf = (filters) => {
  const p = {};
  Object.entries(filters || {}).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) p[k] = v;
  });
  return p;
};

// Load the organization list that powers the top-level filter dropdown.
export const fetchOrganizations = () => async (dispatch) => {
  dispatch({ type: ORGS_REQUEST });
  try {
    const { data } = await api.get("/api/organizations");
    dispatch({ type: ORGS_SUCCESS, payload: data });
  } catch (err) {
    dispatch({ type: ORGS_FAILURE, payload: errorMessage(err) });
  }
};

// Load the cascading location/department/event options for the selected org.
// Called whenever the org changes; the frontend cascades these client-side.
export const fetchFilterOptions = (orgId) => async (dispatch) => {
  dispatch({ type: OPTIONS_REQUEST });
  try {
    const params = orgId ? { org_id: orgId } : {};
    const { data } = await api.get("/api/filters", { params });
    dispatch({ type: OPTIONS_SUCCESS, payload: data });
  } catch (err) {
    dispatch({ type: OPTIONS_FAILURE, payload: errorMessage(err) });
  }
};

// Load every dashboard widget in parallel for the given filter scope.
// Race guard: the deep-dive fetch is slow (seconds), so a newer filter selection
// can resolve first. After awaiting, we drop this response if the live filters no
// longer match the scope this fetch was for — a stale response can never overwrite
// the current selection's data.
export const fetchSummary = (filters) => async (dispatch, getState) => {
  dispatch({ type: SUMMARY_REQUEST });
  const params = paramsOf(filters);
  const sig = JSON.stringify(params);
  const stillCurrent = () => JSON.stringify(paramsOf(getState().ui.filters)) === sig;
  try {
    const [distribution, demographics, assessmentScores, deepDive, typeIndex, required] = await Promise.all([
      api.get("/api/summary/assessment-distribution", { params }),
      api.get("/api/summary/demographics", { params }),
      api.get("/api/summary/assessment-scores", { params }),
      api.get("/api/summary/assessment-deep-dive", { params }),
      api.get("/api/summary/vasana-type-index", { params }),
      api.get("/api/summary/required-assessments", { params }),
    ]);
    if (!stillCurrent()) return; // superseded by a newer filter selection
    dispatch({
      type: SUMMARY_SUCCESS,
      payload: {
        distribution: distribution.data,
        demographics: demographics.data,
        assessmentScores: assessmentScores.data,
        deepDive: deepDive.data,
        typeIndex: typeIndex.data,
        required: required.data,
      },
    });
  } catch (err) {
    if (!stillCurrent()) return; // don't surface an error for a superseded fetch
    dispatch({ type: SUMMARY_FAILURE, payload: errorMessage(err) });
  }
};

function errorMessage(err) {
  return (
    err?.response?.data?.error?.message ||
    err?.message ||
    "Something went wrong while loading data."
  );
}
